import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './rtb/rtb.css';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useRtbMatch } from './rtb/useRtbMatch';
import { RtbBoard } from './rtb/RtbBoard';
import { RtbTopBar, RtbBottomBar, RtbBanner } from './rtb/RtbHud';
import { RulesModal, CombatModal, WinModal, MenuModal, Toasts } from './rtb/RtbOverlays';
import { RtbLobby, RtbWaitingRoom } from './rtb/RtbLobby';
import { isMuted, setMuted } from './rtb/rtbAudio';
import { cityBg, TEAM_COLOR } from './rtb/rtbBoard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/rtb`;

function useConfig() {
  const [cfg, setCfg] = useState({ rules: [], wagers: [1000, 5000, 10000, 25000], cities: [] });
  useEffect(() => { fetch(`${API}/config`).then((r) => r.json()).then(setCfg).catch(() => {}); }, []);
  return cfg;
}

// Fires `fn` once per match version when `ready` holds.
function useStepper(ready, version, fn, delay) {
  const done = useRef(null);
  useEffect(() => {
    if (!ready || done.current === version) return;
    const id = setTimeout(() => { done.current = version; fn(); }, delay);
    return () => clearTimeout(id);
  }, [ready, version]); // eslint-disable-line react-hooks/exhaustive-deps
}


export default function RunTheBlockPremium({ gameId, era = 1, onRefresh }) {
  const g = useRtbMatch(gameId);
  const cfg = useConfig();
  const rootRef = useRef(null);
  const [rules, setRules] = useState(false);
  const [menu, setMenu] = useState(false);
  const [muted, setMute] = useState(isMuted());
  const [expanded, setExpanded] = useState(false);
  const [pickCity, setPickCity] = useState('brooklyn');
  const m = g.match;
  const spectate = !!m?.spectate;
  const idle = !!m && m.status === 'playing' && !g.animating && !g.busy;
  const myTurn = idle && m.turn === g.myTeam;

  useStepper(myTurn && !g.auto && m.phase === 'awaiting_move' && m.movable.length === 1, m?.version, () => g.move(m.movable[0]), 450);
  useStepper(myTurn && g.auto, m?.version, g.advance, 500);
  useStepper(idle && spectate, m?.version, g.advance, 350);

  const playing = m?.status === 'playing';
  useEffect(() => {
    if (!playing) return;
    setExpanded(true);
  }, [playing]);
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [expanded]);
  useEffect(() => { if (m?.status === 'finished' && !g.animating) onRefresh?.(); }, [m?.status, g.animating]); // eslint-disable-line react-hooks/exhaustive-deps

  const shell = 'rtb-root relative w-full overflow-hidden rounded-xl border border-[#C9A84C]/25 bg-[#050507] scroll-mt-16';
  if (g.loading) return <div className={`${shell} h-[420px] flex items-center justify-center text-white/50`} data-testid="rtb-loading">Loading the block…</div>;

  const cityOf = (id) => cfg.cities.find((c) => c.id === (id || 'brooklyn'));
  const cityName = (id) => cityOf(id)?.name;
  const rulesModal = rules && <RulesModal rules={cfg.rules} city={cityOf(m?.city || (m ? 'brooklyn' : pickCity))} onClose={() => setRules(false)} />;
  if (!m || !['playing', 'lobby', 'finished'].includes(m.status)) {
    return (
      <div className={`${shell} min-h-[560px]`} data-testid="rtb-root">
        <div key={pickCity} data-testid="rtb-lobby-backdrop" className="absolute inset-0 rtb-backdrop rtb-toast" style={{ backgroundImage: `url('${cityBg(pickCity)}')` }} /><div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/10" />
        <RtbLobby wagers={cfg.wagers} cities={cfg.cities} era={era} city={pickCity} onCity={setPickCity} busy={g.busy} error={g.error} onCreate={g.create} onJoin={g.join} onRules={() => setRules(true)} />
        {rulesModal}
      </div>
    );
  }
  if (m.status === 'lobby') {
    return (
      <div className={`${shell} min-h-[560px]`} data-testid="rtb-root">
        <div className="absolute inset-0 rtb-backdrop" style={{ backgroundImage: `url('${cityBg(m.city)}')` }} /><div className="absolute inset-0 bg-black/70" />
        <RtbWaitingRoom cityName={cityName(m.city)} match={m} myTeam={g.myTeam} busy={g.busy} onStart={g.start} onLeave={g.leave} />
      </div>
    );
  }

  const turnTeam = g.turn || m.turn;
  const canRoll = myTurn && !g.auto && m.phase === 'awaiting_roll';
  const canEnd = myTurn && !g.auto && m.phase === 'awaiting_move';
  const picking = myTurn && m.phase === 'awaiting_move';
  const status = g.dice.rolling ? 'ROLLING…' : canRoll ? 'POP DICE DOME' : picking ? `MOVE ${m.pending_roll}` : m.status === 'finished' ? 'GAME OVER' : `${turnTeam.toUpperCase()}'S TURN`;
  let banner = null;
  if (m.status === 'finished') banner = null;
  else if (g.dice.rolling) banner = `${g.dice.team?.toUpperCase()} IS ROLLING…`;
  else if (picking && !g.auto) banner = `ROLLED ${m.pending_roll} → PICK A CREW MEMBER TO MOVE ${m.pending_roll} SPACE${m.pending_roll > 1 ? 'S' : ''}`;
  else if (canRoll) banner = 'YOUR TURN → TAP THE DOME TO POP THE DICE';
  else if (spectate) banner = `SPECTATING · ${turnTeam.toUpperCase()} CREW IS UP`;
  else if (g.auto && turnTeam === g.myTeam) banner = 'AUTO-PLAYING YOUR TURN…';
  else banner = `${turnTeam.toUpperCase()} CREW IS MAKING MOVES…`;

  const view = (
    <div ref={rootRef} className={`${shell} flex flex-col ${expanded ? 'rtb-expanded' : 'h-[calc(100dvh-72px)] min-h-[560px] max-h-[1000px]'}`} data-testid="rtb-root">
      {g.display && <RtbTopBar cityName={cityName(m.city)} perk={cityOf(m.city)?.perk} onPerk={() => setRules(true)} match={m} display={g.display} turn={turnTeam} myTeam={g.myTeam} />}
      <div className="relative flex-1 min-h-0">
        {g.display && <RtbBoard city={m.city || 'brooklyn'} layout={cityOf(m.city)?.layout} display={g.display} myTeam={g.myTeam} movable={picking && !g.auto ? m.movable : []} pendingRoll={picking ? m.pending_roll : null}
          turn={turnTeam} dice={g.dice} canRoll={canRoll} onRoll={g.roll} onMove={g.move} fx={g.fx} camera={g.camera} />}
        <RtbBanner text={g.camera ? null : banner} tone={TEAM_COLOR[turnTeam]} />
        <button type="button" data-testid="rtb-expand-btn" onClick={() => setExpanded(!expanded)} aria-label={expanded ? 'Exit full view' : 'Full view'}
          className="rtb-btn-ghost absolute right-2 top-2 z-[99998] w-9 h-9">{expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>
        <div className="absolute inset-x-0 top-14 pointer-events-none"><Toasts toasts={g.toasts} /></div>
        {g.error && <button type="button" data-testid="rtb-error" onClick={() => g.setError(null)} className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[99999] rounded-full bg-[#7f1d1d] px-4 py-1.5 text-xs text-white">{g.error}</button>}
        {g.combat && <CombatModal ev={g.combat} onClose={g.dismissCombat} />}
        {m.status === 'finished' && !g.animating && <WinModal match={m} myTeam={g.myTeam} onDone={g.leave} />}
        {menu && <MenuModal code={m.code} muted={muted} onMute={() => { setMuted(!muted); setMute(!muted); }} speed={g.speed} setSpeed={g.setSpeed} auto={g.auto} onAuto={() => g.setAuto(!g.auto)} spectate={spectate}
          onLeave={async () => { setMenu(false); await g.leave(); onRefresh?.(); }} onClose={() => setMenu(false)} />}
        {rulesModal}
      </div>
      <RtbBottomBar wide={expanded} status={status} onMenu={() => setMenu(true)} onRules={() => setRules(true)} canRoll={canRoll} onRoll={g.roll} canEnd={canEnd} onEnd={g.endTurn}
        speed={g.speed} setSpeed={g.setSpeed} auto={g.auto} onAuto={() => g.setAuto(!g.auto)} spectate={spectate} />
    </div>
  );
  return expanded ? createPortal(view, document.body) : view;
}
